import {
  Player,
  PLAYBACK_STATES,
  TrackInfo,
  TrackType,
  ComposablePlayer,
  PlaybackOptions,
  NetworkRequestObservation,
} from '@quickplay/player-interface';
import { EventEmitter, createEventEmitter } from '@quickplay/foundation';

export function createComposablePlayer(player: Player): ComposablePlayer {
  const eventEmitter: EventEmitter = createEventEmitter();
  const subscribeEvents = {
    trackschanged: (): void => eventEmitter.publish('trackschanged'),
    textchanged: (): void => eventEmitter.publish('textchanged'),
    audiotrackchanged: (): void => eventEmitter.publish('audiotrackchanged'),
    trackavailabilitychanged: (tracks: TrackInfo[]): void => eventEmitter.publish('trackavailabilitychanged', tracks),
    licenseupdate: (): void => eventEmitter.publish('licenseupdate'),
    timedmetadata: (event: any): void => eventEmitter.publish('timedmetadata', event),
    bandwidthchanged: (): void => eventEmitter.publish('bandwidthchanged'),
    daitimelineregionenter: (event: any): void => eventEmitter.publish('daitimelineregionenter', event),
    error: (error: any): void => eventEmitter.publish('error', error),

    playbackstatechanged: (playerState: any): void => {
      eventEmitter.publish('statechangestep', player);
      resetOnPlayerStopped(playerState);
    },
    engagementscore: (score: any): void => eventEmitter.publish('engagementscore', score),
    progressupdate: (): void => eventEmitter.publish('heartbeatstep', player),
    ratechanged: (): void => eventEmitter.publish('ratechanged'),
    volumechanged: (): void => eventEmitter.publish('volumechanged'),
    manifestupdated: (): void => eventEmitter.publish('manifestupdated'),
    manifestparsed: (): void => eventEmitter.publish('manifestparsed'),
    networkrequest: (observation: NetworkRequestObservation): void => eventEmitter.publish('networkrequest', observation),
    networkresponse: (observation: NetworkRequestObservation): void => eventEmitter.publish('networkresponse', observation),
  };

  player.subscribe(subscribeEvents);

  const composablePlayer: ComposablePlayer = {
    subscribe: eventEmitter.subscribe,

    unsubscribe: eventEmitter.unsubscribe,

    internalPlayerVersion: player.internalPlayerVersion,

    sdkVersion: player.sdkVersion,

    internalPlayerName: player.internalPlayerName,

    rawPlayer: player.rawPlayer,

    rawLibrary: player.rawLibrary,

    isBuffering: player.isBuffering,

    isSeeking: player.isSeeking,

    currentTime: player.currentTime,

    currentEpochTime: player.currentEpochTime,

    duration: player.duration,

    playbackStatistics: player.playbackStatistics,

    playbackRate: player.playbackRate,

    availableBitrates: player.availableBitrates,

    minBitrate: player.minBitrate,

    maxBitrate: player.maxBitrate,

    playerState: player.playerState,

    stop: player.stop,

    play: player.play,

    pause: player.pause,

    seek: player.seek,

    getThumbnail: player.getThumbnail,

    abort: player.abort,

    send: player.send,

    isLive: player.isLive,

    isMute: player.isMute,

    volume: player.volume,

    playbackOptions: player.playbackOptions,

    muteOrUnmute: player.muteOrUnmute,

    seekableRange: player.seekableRange,

    seekToLiveEdge: player.seekToLiveEdge,

    currentOffsetFromLiveEdge: player.currentOffsetFromLiveEdge,

    getAllTracks: player.getAllTracks,

    getActiveTracks: player.getActiveTracks,

    getVariantTracks: player.getVariantTracks,

    getAvailableTrackVariants: player.getAvailableTrackVariants,

    selectTrack: player.selectTrack,

    setRequestInterceptor: player.setRequestInterceptor,

    setResponseInterceptor: player.setResponseInterceptor,

    accessFunctionHandler: player.accessFunctionHandler,
    load: () => {
      eventEmitter.publish('preloadstep', player);
      player.load();
    },

    addPreLoadStep(preLoadStep: (player: Player) => void): void {
      this.subscribe('preloadstep', preLoadStep);
    },

    addStateChangeStep(stateChangeStep: (player: Player) => void): void {
      this.subscribe('statechangestep', stateChangeStep);
    },

    addHeartBeatStep(heartBeatStep: (player: Player) => void): void {
      this.subscribe('heartbeatstep', heartBeatStep);
    },

    setPlaybackOptions(playbackOptions: PlaybackOptions): void {
      player.setPlaybackOptions(playbackOptions);
    },

    restrictQuality(enableRestriction: boolean): void {
      player.restrictQuality(enableRestriction);
    },

    availableMimeTypes(trackType: TrackType): Array<string> {
      return player.availableMimeTypes(trackType);
    },

    unloadAndSavePreload(): Promise<void> {
      return Promise.resolve();
    },

    setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
      return player.setPreferredMimeTypes(trackType, mimeTypes);
    },

    restoreFromPreload(): void {
      // no-op
    },
  };

  function resetOnPlayerStopped(playerState: any): void {
    Object.entries(playerState).forEach(([stateKey, stateValue]) => {
      if (stateKey === 'playing' && stateValue === PLAYBACK_STATES.IDLE) {
        eventEmitter.reset();
        player.unsubscribe(subscribeEvents);
      }
    });
  }
  return composablePlayer;
}
